import { type CreateSetup, api } from '@here/api'
import * as v from 'valibot'

export type imageProperties = Pick<
	CreateSetup,
	| 'image_macro'
	| 'image_pattern'
	| 'image_trend'
	| 'image_trigger'
	| 'image_time_stop'
	| 'image_volume_by_price'
>

export const handleCreateSetup =
	(onCreateSuccess: () => void, onCreateError: (message: string) => void) =>
	async (setup?: CreateSetup, oldSetup?: CreateSetup) => {
		if (setup === undefined) {
			return
		}

		const isEditing = Boolean(oldSetup)

		if (!isEditing) {
			delete setup.id
		}

    setup.symbol = setup.symbol.toUpperCase().trim();

		const properties: Array<keyof imageProperties> = [
			'image_macro',
			'image_pattern',
			'image_trend',
			'image_trigger',
			'image_time_stop',
			'image_volume_by_price',
		]

		const uploadedFiles: { key: keyof imageProperties; path: string }[] = []

		const allImages: { key: keyof imageProperties; path: string }[] = [
			{ key: 'image_macro', path: setup.image_macro },
			{ key: 'image_pattern', path: setup.image_pattern },
			{ key: 'image_trend', path: setup.image_trend },
			{ key: 'image_trigger', path: setup.image_trigger },
			{ key: 'image_time_stop', path: setup.image_time_stop || '' },
			{ key: 'image_volume_by_price', path: setup.image_volume_by_price || '' },
		]

		// Delete images that have been replaced
		const images = isEditing
			? allImages.filter((value) => {
					const uuidSchema = v.pipe(
						v.string(),
						v.minLength(1),
						v.regex(
							/^(https:\/\/s3\.tradingview\.com\/snapshots\/[A-Za-z0-9]\/[A-Za-z0-9]+\.png)$/,
							'The image must be a TradingView URL',
						),
					)
					return v.safeParse(uuidSchema, value.path).success
				})
			: allImages.filter((value) => {
					const uuidSchema = v.pipe(
						v.string(),
						v.regex(
							/^(https:\/\/s3\.tradingview\.com\/snapshots\/[A-Za-z0-9]\/[A-Za-z0-9]+\.png)$/,
							'The image must be a TradingView URL',
						),
					)
					return v.safeParse(uuidSchema, value.path).success
				})

		try {
			// Step 1: Download and upload each image
			const uploadPromises = images.map(async (val) => {
				// Download image
				const response = await fetch(val.path)
				if (!response.ok)
					throw new Error(`Failed to download ${val.key} from ${val.path}`)

				const blob = await response.blob()
				const fileExt = val.path.split('.').pop() || 'png'
				const filePath = `${crypto.randomUUID()}.${fileExt}`

				// Upload to Supabase
				const { error: uploadError } = await api()
					.storage.from('setup-images')
					.upload(filePath, blob, { contentType: `image/${fileExt}` })

				if (uploadError) throw new Error(uploadError.message)

				uploadedFiles.push(val)

				// Return the public URL
				return { key: val.key, url: filePath }
			})

			// delete the old images
			if (isEditing) {
				const filesToDelete = images
					.filter(({ key }) => Boolean(oldSetup![key]))
					.map(({ key }) => oldSetup![key]!)
				if (filesToDelete.length) {
					await api().storage.from('setup-images').remove(filesToDelete)
				}
			}

			/**
			 * Waits for all image upload promises to resolve, filters out any falsy values,
			 * and casts the result to an array of objects containing image properties.
			 *
			 * @returns {Promise<{ key: keyof imageProperties; url: string }[]>}
			 * An array of objects each containing a key and a URL of the uploaded image.
			 */
			const uploadedImages = (await Promise.all(uploadPromises)).filter(Boolean) as {
				key: keyof imageProperties
				url: string
			}[]

			// Step 2: Insert into 'setup' table with new image URLs
			for (const { key, url } of uploadedImages) {
				setup[key] = url
			}

			const { data, error: insertError } = setup.id
				? await api().from('setup').update(setup).eq('id', setup.id)
				: await api().from('setup').insert(setup)

			if (insertError) {
				// Cleanup if insert fails
				await api()
					.storage.from('setup-images')
					.remove(uploadedFiles.map((f) => f.path))
				throw new Error(insertError.message)
			}

			onCreateSuccess()
			return data
		} catch (err) {
			// Cleanup uploaded files if any error occurs
			if (uploadedFiles.length > 0) {
				await api()
					.storage.from('setup-images')
					.remove(uploadedFiles.map((f) => f.path))
			}
			onCreateError(err instanceof Error ? err.message : 'An error occurred')
			return null
		}
	}
